/* ==========================================================================
   Chic Bridals — Booking quiz
   ==========================================================================

   Drives the branching "Find Your Chic Bridal Experience" flow rendered by
   sections/booking-quiz.liquid.

   The question tree is not defined here. It is read from the DOM that Liquid
   renders, so the whole flow stays editable in the theme editor:

     [data-panel][data-node-id]        a question or a result
     [data-quiz-option][data-target]   an answer, pointing at the next node id
     [data-tag]                        a backend flag carried down the path

   Public surface, for pixels / apps that need the outcome:
     document event  'chic:quiz:step'    -> detail { quizId, nodeId, path }
     document event  'chic:quiz:result'  -> detail { quizId, resultId, name, tags, path }
     localStorage    'chic:booking-quiz'
     window.dataLayer push (GA4 / GTM) when present
   ========================================================================== */

(function () {
  'use strict';

  var STORAGE_KEY = 'chic:booking-quiz';
  var HASH_KEY = 'experience';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Height of the sticky header in pixels. global.js writes --header-height in
     px, but the CSS fallback is declared in rem, so both units are handled.
     Root font-size is 62.5%, so 1rem = 10px. */
  function headerOffset() {
    var value = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    var parsed = parseFloat(value);
    if (isNaN(parsed)) return 0;
    return value.indexOf('rem') > -1 ? parsed * 10 : parsed;
  }

  class BookingQuiz extends HTMLElement {
    constructor() {
      super();
      this.nodes = new Map();
      this.depthCache = new Map();
      this.path = [];
      this.currentId = null;
      this.initialised = false;
    }

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.quizId = this.dataset.quizId || 'booking-quiz';
      this.paramPrefix = this.dataset.paramPrefix || 'chic';
      this.trackingEnabled = this.dataset.tracking !== 'false';
      this.syncHistory = this.dataset.history !== 'false';

      this.stage = this.querySelector('[data-quiz-stage]');
      this.bar = this.querySelector('[data-quiz-bar]');
      this.rail = this.querySelector('[data-quiz-rail]');
      this.counter = this.querySelector('[data-quiz-counter]');
      this.backButton = this.querySelector('[data-quiz-back]');
      this.restartButton = this.querySelector('[data-quiz-restart-bar]');
      this.crumbs = this.querySelector('[data-quiz-crumbs]');
      this.live = this.querySelector('[data-quiz-live]');

      this.counterTemplate = (this.counter && this.counter.dataset.template) || 'Step [current] of [total]';

      this.buildGraph();
      if (!this.nodes.size) return;

      this.startId = this.resolveStartId();
      this.bindEvents();

      var restored = this.readHash();
      if (restored.length && this.restore(restored)) {
        this.render({ animate: false, focus: false, scroll: false });
      } else {
        this.goTo(this.startId, { push: false, focus: false, scroll: false, animate: false });
      }
    }

    /* ----------------------------------------------------------------------
       Graph
       ---------------------------------------------------------------------- */

    buildGraph() {
      var self = this;

      this.querySelectorAll('[data-panel]').forEach(function (panel) {
        var id = panel.dataset.nodeId;
        if (!id || self.nodes.has(id)) return;

        var options = [];
        panel.querySelectorAll('[data-quiz-option]').forEach(function (button) {
          if (!button.dataset.target) return;
          options.push({
            el: button,
            target: button.dataset.target,
            label: button.dataset.label || button.textContent.trim(),
          });
        });

        self.nodes.set(id, {
          id: id,
          el: panel,
          type: panel.dataset.panelType === 'result' ? 'result' : 'step',
          tag: panel.dataset.tag || '',
          name: panel.dataset.nodeName || '',
          options: options,
        });
      });

      /* Drop answers that point at a node which does not exist, so a typo in
         the editor degrades to a hidden option instead of a dead end. */
      this.nodes.forEach(function (node) {
        node.options = node.options.filter(function (option) {
          if (self.nodes.has(option.target)) return true;
          option.el.hidden = true;
          return false;
        });
      });
    }

    resolveStartId() {
      var requested = this.dataset.start;
      if (requested && this.nodes.has(requested)) return requested;

      var firstStep = null;
      this.nodes.forEach(function (node) {
        if (!firstStep && node.type === 'step') firstStep = node.id;
      });
      return firstStep || this.nodes.keys().next().value;
    }

    /* Longest remaining run of questions, used for the progress bar. */
    maxDepth(id) {
      if (this.depthCache.has(id)) return this.depthCache.get(id);

      var node = this.nodes.get(id);
      if (!node || node.type === 'result') return 0;

      this.depthCache.set(id, 1); // cycle guard
      var deepest = 0;
      var self = this;
      node.options.forEach(function (option) {
        var depth = self.maxDepth(option.target);
        if (depth > deepest) deepest = depth;
      });

      var total = 1 + deepest;
      this.depthCache.set(id, total);
      return total;
    }

    /* ----------------------------------------------------------------------
       Events
       ---------------------------------------------------------------------- */

    bindEvents() {
      var self = this;

      this.addEventListener('click', function (event) {
        var option = event.target.closest('[data-quiz-option]');
        if (option && self.contains(option)) {
          event.preventDefault();
          self.answer(option);
          return;
        }

        var restart = event.target.closest('[data-quiz-restart], [data-quiz-restart-bar]');
        if (restart && self.contains(restart)) {
          event.preventDefault();
          self.restart();
          return;
        }

        var back = event.target.closest('[data-quiz-back]');
        if (back && self.contains(back)) {
          event.preventDefault();
          self.back();
          return;
        }

        var crumb = event.target.closest('[data-quiz-crumb]');
        if (crumb && self.contains(crumb)) {
          event.preventDefault();
          self.jumpTo(parseInt(crumb.dataset.quizCrumb, 10));
        }
      });

      if (this.syncHistory) {
        this.onPopState = function () {
          var ids = self.readHash();
          if (ids.length && self.restore(ids)) {
            self.render({ animate: true, focus: false });
          } else {
            self.path = [];
            self.currentId = self.startId;
            self.render({ animate: true, focus: false });
          }
        };
        window.addEventListener('popstate', this.onPopState);
      }

      /* Theme editor: selecting a block reveals that question or result. */
      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = function (event) {
          var panel = self.querySelector('[data-block-id="' + event.detail.blockId + '"]');
          if (!panel || !panel.dataset.nodeId) return;
          self.currentId = panel.dataset.nodeId;
          self.render({ animate: true, focus: false, scroll: false });
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      if (this.onPopState) window.removeEventListener('popstate', this.onPopState);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* ----------------------------------------------------------------------
       Navigation
       ---------------------------------------------------------------------- */

    answer(button) {
      var target = button.dataset.target;
      if (!this.nodes.has(target)) return;

      this.path.push({
        stepId: this.currentId,
        target: target,
        label: button.dataset.label || button.textContent.trim(),
      });

      this.goTo(target, { push: true });
    }

    back() {
      if (!this.path.length) return;
      var previous = this.path.pop();
      this.goTo(previous.stepId, { push: true });
    }

    /* Rewinds to the question that produced breadcrumb `index`. */
    jumpTo(index) {
      if (isNaN(index) || index < 0 || index >= this.path.length) return;
      var entry = this.path[index];
      this.path = this.path.slice(0, index);
      this.goTo(entry.stepId, { push: true });
    }

    restart() {
      this.path = [];
      this.goTo(this.startId, { push: true });
    }

    /* Entry point for the popup: jump straight to a question, e.g. a VIP link
       that should land on the VIP finder rather than the first question. */
    openAt(id) {
      if (!this.nodes.has(id)) return false;
      this.path = [];
      this.goTo(id, { push: false, focus: false, scroll: false });
      return true;
    }

    goTo(id, options) {
      if (!this.nodes.has(id)) return;
      options = options || {};

      this.currentId = id;
      this.render({
        animate: options.animate !== false,
        focus: options.focus !== false,
        scroll: options.scroll !== false,
      });

      if (options.push !== false) this.writeHash();
    }

    /* ----------------------------------------------------------------------
       Render
       ---------------------------------------------------------------------- */

    render(options) {
      options = options || {};

      var node = this.nodes.get(this.currentId);
      if (!node) return;

      this.nodes.forEach(function (candidate) {
        candidate.el.hidden = candidate.id !== node.id;
        candidate.el.removeAttribute('data-enter');
      });

      if (options.animate && !prefersReducedMotion()) {
        void node.el.offsetWidth; // restart the entrance animation
        node.el.setAttribute('data-enter', '');
      }

      this.renderProgress(node);
      this.renderCrumbs();

      if (node.type === 'result') {
        this.handleResult(node);
      } else {
        this.emit('chic:quiz:step', { quizId: this.quizId, nodeId: node.id, path: this.serialize() });
      }

      if (this.live) {
        var heading = node.el.querySelector('[data-quiz-title]');
        this.live.textContent = heading ? heading.textContent.trim() : '';
      }

      if (options.focus) this.focusPanel(node);
      if (options.scroll) this.scrollIntoViewIfNeeded();
    }

    renderProgress(node) {
      var answered = this.path.length;

      if (this.backButton) this.backButton.hidden = answered === 0;
      if (this.restartButton) this.restartButton.hidden = answered === 0;

      if (node.type === 'result') {
        if (this.rail) this.rail.style.setProperty('--bq-progress', '100%');
        if (this.counter) this.counter.hidden = true;
        return;
      }

      var total = answered + this.maxDepth(node.id);
      var current = answered + 1;
      if (total < current) total = current;

      if (this.rail) {
        this.rail.style.setProperty('--bq-progress', Math.round((current / total) * 100) + '%');
      }

      if (this.counter) {
        this.counter.hidden = false;
        this.counter.textContent = this.counterTemplate
          .replace('[current]', current)
          .replace('[total]', total);
      }
    }

    renderCrumbs() {
      if (!this.crumbs) return;

      this.crumbs.innerHTML = '';
      if (!this.path.length) {
        this.crumbs.hidden = true;
        return;
      }

      this.crumbs.hidden = false;
      var self = this;

      this.path.forEach(function (entry, index) {
        var item = document.createElement('li');
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'booking-quiz__crumb';
        button.dataset.quizCrumb = index;
        button.textContent = entry.label;
        button.setAttribute('aria-label', 'Change answer: ' + entry.label);
        item.appendChild(button);
        self.crumbs.appendChild(item);
      });
    }

    focusPanel(node) {
      var heading = node.el.querySelector('[data-quiz-title]');
      if (heading && heading.focus) heading.focus({ preventScroll: true });
    }

    scrollIntoViewIfNeeded() {
      /* Inside the popup the quiz scrolls its own container, not the page. */
      var scroller = this.closest('[data-quiz-modal-body]');
      if (scroller) {
        if (scroller.scrollTop === 0) return;
        scroller.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        return;
      }

      var top = this.getBoundingClientRect().top;
      var offset = headerOffset() + 24;
      if (top >= offset) return;

      window.scrollTo({
        top: window.pageYOffset + top - offset,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    /* ----------------------------------------------------------------------
       Result handling — tags, booking links, tracking
       ---------------------------------------------------------------------- */

    collectTags(node) {
      var tags = [];
      var self = this;

      this.path.forEach(function (entry) {
        var step = self.nodes.get(entry.stepId);
        if (step && step.tag) tags.push(step.tag);
      });

      if (node.tag) tags.push(node.tag);

      return tags.filter(function (tag, index) {
        return tags.indexOf(tag) === index;
      });
    }

    handleResult(node) {
      var tags = this.collectTags(node);
      var payload = {
        quizId: this.quizId,
        resultId: node.id,
        name: node.name,
        tags: tags,
        path: this.serialize(),
        answers: this.path.map(function (entry) {
          return { question: entry.stepId, answer: entry.label };
        }),
      };

      this.decorateBookingLinks(node, payload);
      this.persist(payload);
      this.emit('chic:quiz:result', payload);

      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({
          event: 'chic_quiz_result',
          quiz_id: payload.quizId,
          quiz_result: payload.resultId,
          quiz_result_name: payload.name,
          quiz_tags: tags.join('|'),
          quiz_path: payload.path.join('>'),
        });
      }
    }

    /* Carries the quiz outcome into the booking URL so the team can see which
       route a bride took. Booking hosts ignore unknown params, and the values
       survive into referral analytics. */
    decorateBookingLinks(node, payload) {
      if (!this.trackingEnabled) return;

      var prefix = this.paramPrefix;
      var self = this;

      node.el.querySelectorAll('[data-quiz-book]').forEach(function (link) {
        if (!link.dataset.href) link.dataset.href = link.getAttribute('href') || '';
        var base = link.dataset.href;
        if (!base || base === '#') return;

        var url;
        try {
          url = new URL(base, window.location.origin);
        } catch (error) {
          return;
        }

        url.searchParams.set('utm_source', window.location.hostname);
        url.searchParams.set('utm_medium', 'booking-quiz');
        url.searchParams.set('utm_campaign', payload.resultId);
        url.searchParams.set(prefix + '_quiz', self.quizId);
        url.searchParams.set(prefix + '_path', payload.path.join('>'));
        if (payload.tags.length) url.searchParams.set(prefix + '_tag', payload.tags.join('|'));

        link.setAttribute('href', url.toString());
      });
    }

    persist(payload) {
      try {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            resultId: payload.resultId,
            name: payload.name,
            tags: payload.tags,
            path: payload.path,
            answers: payload.answers,
            savedAt: new Date().toISOString(),
          })
        );
      } catch (error) {
        /* Private browsing or a full quota — tracking is best-effort. */
      }
    }

    emit(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
    }

    /* ----------------------------------------------------------------------
       History
       ---------------------------------------------------------------------- */

    serialize() {
      return this.path.map(function (entry) {
        return entry.target;
      });
    }

    readHash() {
      if (!this.syncHistory) return [];
      var match = window.location.hash.match(new RegExp('[#&]' + HASH_KEY + '=([^&]+)'));
      if (!match) return [];
      return decodeURIComponent(match[1]).split('/').filter(Boolean);
    }

    writeHash() {
      if (!this.syncHistory) return;

      var ids = this.serialize();
      var url = window.location.pathname + window.location.search;
      if (ids.length) url += '#' + HASH_KEY + '=' + encodeURIComponent(ids.join('/'));

      try {
        window.history.pushState({ quiz: this.quizId, path: ids }, '', url);
      } catch (error) {
        /* Sandboxed preview frames can refuse pushState — navigation still works. */
      }
    }

    /* Replays a saved route, rebuilding the answer labels and tag trail. */
    restore(ids) {
      var cursor = this.startId;
      var rebuilt = [];

      for (var i = 0; i < ids.length; i++) {
        var node = this.nodes.get(cursor);
        if (!node || node.type === 'result') return false;

        var match = null;
        for (var j = 0; j < node.options.length; j++) {
          if (node.options[j].target === ids[i]) {
            match = node.options[j];
            break;
          }
        }
        if (!match) return false;

        rebuilt.push({ stepId: cursor, target: match.target, label: match.label });
        cursor = match.target;
      }

      this.path = rebuilt;
      this.currentId = cursor;
      return true;
    }
  }

  if (!customElements.get('booking-quiz')) {
    customElements.define('booking-quiz', BookingQuiz);
  }

  /* ------------------------------------------------------------------------
     Popup wrapper
     ------------------------------------------------------------------------ */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function lockScroll(el) {
    if (window.bodyScrollLock && window.bodyScrollLock.disableBodyScroll) {
      window.bodyScrollLock.disableBodyScroll(el, { reserveScrollBarGap: true });
    } else {
      document.documentElement.style.overflow = 'hidden';
    }
  }

  function unlockScroll(el) {
    if (window.bodyScrollLock && window.bodyScrollLock.enableBodyScroll) {
      window.bodyScrollLock.enableBodyScroll(el);
    } else {
      document.documentElement.style.overflow = '';
    }
  }

  class BookingQuizModal extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.launcher = this.querySelector('[data-quiz-launcher]');
      this.modal = this.querySelector('[data-quiz-modal]');
      this.body = this.querySelector('[data-quiz-modal-body]');
      if (!this.modal) return;

      this.isOpen = false;
      this.lastFocused = null;

      /* Each entry is "/path" or "/path=nodeId". The optional node opens the
         quiz on that question instead of the first one. */
      this.openPaths = (this.dataset.openFrom || '')
        .split(',')
        .map(function (entry) {
          var parts = entry.split('=');
          return {
            path: parts[0].trim().replace(/\/$/, ''),
            node: (parts[1] || '').trim(),
          };
        })
        .filter(function (entry) {
          return entry.path;
        });

      this.bind();
    }

    bind() {
      var self = this;

      if (this.launcher) {
        /* Pass the launcher explicitly so focus returns to it on close,
           rather than depending on the click having focused it. */
        this.launcher.addEventListener('click', function () {
          self.open(self.launcher);
        });
      }

      this.modal.addEventListener('click', function (event) {
        if (event.target.closest('[data-quiz-modal-close]')) {
          event.preventDefault();
          self.close();
        }
      });

      this.onKeydown = function (event) {
        if (!self.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          self.close();
          return;
        }
        if (event.key === 'Tab') self.trapFocus(event);
      };
      document.addEventListener('keydown', this.onKeydown);

      /* Links pointing at the quiz page open the popup instead. Without JS
         they still navigate, so the standalone page stays a real fallback. */
      this.onDocumentClick = function (event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        var opener = event.target.closest('[data-booking-quiz-open]');
        var node = opener ? opener.getAttribute('data-booking-quiz-open') : '';

        if (!opener) {
          var link = event.target.closest('a[href]');
          if (!link || !self.openPaths.length) return;
          if (link.target && link.target !== '_self') return;

          var path;
          try {
            path = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
          } catch (error) {
            return;
          }

          var match = null;
          for (var i = 0; i < self.openPaths.length; i++) {
            if (self.openPaths[i].path === path) {
              match = self.openPaths[i];
              break;
            }
          }
          if (!match) return;

          opener = link;
          node = match.node;
        }

        event.preventDefault();
        /* A link is an invitation to begin, so it always starts fresh —
           unlike the floating button, which resumes where she left off. */
        self.open(opener, { node: node, reset: true });
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      if (this.isOpen) unlockScroll(this.body || this.modal);
    }

    open(opener, options) {
      if (this.isOpen) return;
      options = options || {};
      this.isOpen = true;
      this.lastFocused = opener || document.activeElement;

      var quiz = this.querySelector('booking-quiz');
      if (quiz) {
        if (options.node && typeof quiz.openAt === 'function') {
          if (!quiz.openAt(options.node) && options.reset) quiz.restart();
        } else if (options.reset && typeof quiz.restart === 'function') {
          quiz.restart();
        }
      }

      window.clearTimeout(this.closeTimer);
      this.modal.hidden = false;
      void this.modal.offsetWidth; // let the transition run from the closed state
      this.modal.classList.add('is-open');
      document.documentElement.classList.add('booking-quiz-modal-open');
      if (this.launcher) this.launcher.setAttribute('aria-expanded', 'true');

      lockScroll(this.body || this.modal);
      if (this.body) this.body.scrollTop = 0;

      var target =
        this.modal.querySelector('[data-panel]:not([hidden]) [data-quiz-title]') ||
        this.modal.querySelector('[data-quiz-modal-close]');
      if (target && target.focus) target.focus({ preventScroll: true });

      document.dispatchEvent(
        new CustomEvent('chic:quiz:open', { detail: { quizId: this.id || null }, bubbles: true })
      );
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.modal.classList.remove('is-open');
      document.documentElement.classList.remove('booking-quiz-modal-open');
      if (this.launcher) this.launcher.setAttribute('aria-expanded', 'false');
      unlockScroll(this.body || this.modal);

      var self = this;
      var finish = function () {
        if (!self.isOpen) self.modal.hidden = true;
      };

      if (prefersReducedMotion()) {
        finish();
      } else {
        this.closeTimer = window.setTimeout(finish, 300);
      }

      if (this.lastFocused && this.lastFocused.focus) {
        this.lastFocused.focus({ preventScroll: true });
      }
    }

    trapFocus(event) {
      var nodes = Array.prototype.filter.call(
        this.modal.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null;
        }
      );
      if (!nodes.length) return;

      var first = nodes[0];
      var last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('booking-quiz-modal')) {
    customElements.define('booking-quiz-modal', BookingQuizModal);
  }
})();
